<script setup lang="ts">
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import type { ShipmentExceptionFeedback } from '@gpx-spa/queries/src/shipment-exception-types';

const props = defineProps<{
  visible: boolean;
  count?: number;
  isSubmitting?: boolean;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  confirm: [
    payload: { feedback: ShipmentExceptionFeedback | null; note: string },
  ];
}>();

const note = ref('');

watch(
  () => props.visible,
  (open) => {
    if (open) {
      note.value = '';
    }
  },
);

function close() {
  emit('update:visible', false);
}

function submit(feedback: ShipmentExceptionFeedback) {
  emit('confirm', {
    feedback,
    note: note.value.trim(),
  });
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    header="Close exception"
    class="w-full max-w-xl"
    :style="{ width: '36rem' }"
    :closable="!isSubmitting"
    @update:visible="emit('update:visible', $event)"
  >
    <p class="mb-3 text-sm text-neutral-700">
      <template v-if="(count ?? 1) > 1">
        Close {{ count }} exceptions. Optionally add a note, then choose how to
        close.
      </template>
      <template v-else>
        Optionally add a note, then choose how to close this exception.
      </template>
    </p>

    <label class="mb-1 block text-xs font-medium text-neutral-600"
      >Note (optional)</label
    >
    <Textarea
      v-model="note"
      rows="3"
      class="w-full"
      placeholder="Optional context for GPX"
      :disabled="isSubmitting"
    />

    <template #footer>
      <div class="flex flex-nowrap items-center justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          text
          size="small"
          :disabled="isSubmitting"
          @click="close"
        />
        <Button
          label="Not an issue"
          severity="success"
          outlined
          size="small"
          class="whitespace-nowrap"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit('not_an_issue')"
        />
        <Button
          label="Real issue"
          severity="danger"
          size="small"
          class="whitespace-nowrap"
          :loading="isSubmitting"
          :disabled="isSubmitting"
          @click="submit('real_issue')"
        />
      </div>
    </template>
  </Dialog>
</template>
